import { extractHtmlError, fetchWithXsrf, getAntiForgeryToken } from "@siisel/frontend-shared";
import { useState } from "react";
import type { BackgroundJobsWarmupFormViewModel } from "../types";

function appendBoolean(formData: FormData, name: string, value: boolean) {
  formData.append(name, value ? "true" : "false");
}

type BooleanFieldProps = {
  id: string;
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: (value: boolean) => void;
};

function BooleanField({ id, label, checked, disabled, onChange }: BooleanFieldProps) {
  return (
    <div className="form-group forms-app-toggle-row">
      <label htmlFor={id}>{label}</label>
      <label className="forms-app-toggle">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.currentTarget.checked)}
        />
        <span>{checked ? "Ano" : "Ne"}</span>
      </label>
    </div>
  );
}

export default function BackgroundJobsWarmupFormApp(props: BackgroundJobsWarmupFormViewModel) {
  const [useReadonlyGisWarmupJob, setUseReadonlyGisWarmupJob] = useState(props.values.useReadonlyGisWarmupJob);
  const [readonlyGisWarmupCronExpression, setReadonlyGisWarmupCronExpression] = useState(props.values.readonlyGisWarmupCronExpression);
  const [useReadonlyChartWarmupJob, setUseReadonlyChartWarmupJob] = useState(props.values.useReadonlyChartWarmupJob);
  const [readonlyChartWarmupCronExpression, setReadonlyChartWarmupCronExpression] = useState(props.values.readonlyChartWarmupCronExpression);
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  function createFormData(root: HTMLElement | null): FormData {
    const formData = new FormData();
    const token = getAntiForgeryToken(root);
    if (token) {
      formData.append("__RequestVerificationToken", token);
    }

    appendBoolean(formData, "UseReadonlyGisWarmupJob", useReadonlyGisWarmupJob);
    formData.append("ReadonlyGisWarmupCronExpression", readonlyGisWarmupCronExpression);
    appendBoolean(formData, "UseReadonlyChartWarmupJob", useReadonlyChartWarmupJob);
    formData.append("ReadonlyChartWarmupCronExpression", readonlyChartWarmupCronExpression);
    formData.append("saveAndClose", "false");
    return formData;
  }

  async function post(actionName: string, url: string, root: HTMLElement | null, checkValidation: boolean) {
    if (pendingAction) {
      return;
    }

    setPendingAction(actionName);
    setErrorMessage(null);

    try {
      const response = await fetchWithXsrf(url, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "text/html"
        },
        body: createFormData(root)
      });
      const html = await response.text();

      if (!response.ok) {
        throw new Error(html);
      }

      if (checkValidation) {
        const validationMessage = extractHtmlError(html);
        if (validationMessage) {
          setErrorMessage(validationMessage);
          return;
        }
      }

      window.location.reload();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Akci se nepodarilo provest.";
      setErrorMessage(message.includes("<") ? extractHtmlError(message) || message : message);
    } finally {
      setPendingAction(null);
    }
  }

  const disabled = Boolean(pendingAction);

  return (
    <div className="forms-app background-jobs-warmup-form-app">
      {errorMessage ? <div className="alert alert-danger">{errorMessage}</div> : null}
      <div className="base-form">
        <div className="inner-form">
          <BooleanField
            id="warmup-use-readonly-gis"
            label={props.labels.useReadonlyGisWarmupJob}
            checked={useReadonlyGisWarmupJob}
            disabled={disabled}
            onChange={setUseReadonlyGisWarmupJob}
          />
          <div className="form-group">
            <label htmlFor="warmup-gis-cron">{props.labels.readonlyGisWarmupCronExpression}</label>
            <input
              id="warmup-gis-cron"
              className="form-control"
              value={readonlyGisWarmupCronExpression}
              disabled={disabled}
              onChange={(event) => {
                setReadonlyGisWarmupCronExpression(event.currentTarget.value);
                setErrorMessage(null);
              }}
            />
            <div className="help-block">{props.labels.gisCronHelp}</div>
          </div>
          <BooleanField
            id="warmup-use-readonly-chart"
            label={props.labels.useReadonlyChartWarmupJob}
            checked={useReadonlyChartWarmupJob}
            disabled={disabled}
            onChange={setUseReadonlyChartWarmupJob}
          />
          <div className="form-group">
            <label htmlFor="warmup-chart-cron">{props.labels.readonlyChartWarmupCronExpression}</label>
            <input
              id="warmup-chart-cron"
              className="form-control"
              value={readonlyChartWarmupCronExpression}
              disabled={disabled}
              onChange={(event) => {
                setReadonlyChartWarmupCronExpression(event.currentTarget.value);
                setErrorMessage(null);
              }}
            />
            <div className="help-block">{props.labels.chartCronHelp}</div>
          </div>
        </div>
      </div>

      <div className="panel-footer">
        <button
          type="button"
          className="btn btn-success btn-sm"
          disabled={disabled}
          onClick={(event) => void post("save", props.saveUrl, event.currentTarget.closest(".forms-app"), true)}
        >
          <em className="fa fa-save"></em>
          {pendingAction === "save" ? `${props.submitText}...` : props.submitText}
        </button>
        <button
          type="button"
          className="btn btn-default btn-sm"
          disabled={disabled}
          onClick={(event) => void post("runGis", props.runReadonlyGisWarmupUrl, event.currentTarget.closest(".forms-app"), false)}
        >
          <em className="fa fa-play"></em>
          {pendingAction === "runGis" ? `${props.runGisText}...` : props.runGisText}
        </button>
        <button
          type="button"
          className="btn btn-default btn-sm"
          disabled={disabled}
          onClick={(event) => void post("runChart", props.runReadonlyChartWarmupUrl, event.currentTarget.closest(".forms-app"), false)}
        >
          <em className="fa fa-play"></em>
          {pendingAction === "runChart" ? `${props.runChartText}...` : props.runChartText}
        </button>
      </div>
    </div>
  );
}
